import WebMap from '@arcgis/core/WebMap'
import WebScene from '@arcgis/core/WebScene'
import type { ViewMode } from '@/stores/uiStore'

/**
 * Singleton manager for WebMap and WebScene instances
 * Ensures maps are created only once and reused across view mode switches
 */
class MapInstanceManager {
  private static instance: MapInstanceManager
  private webMap: WebMap | null = null
  private webScene: WebScene | null = null
  private initialized = false

  private constructor() {}

  static getInstance(): MapInstanceManager {
    if (!MapInstanceManager.instance) {
      MapInstanceManager.instance = new MapInstanceManager()
    }
    return MapInstanceManager.instance
  }

  /**
   * Initialize both WebMap and WebScene instances
   * Called once on first access
   */
  private initialize(): void {
    if (this.initialized) return

    const webMapId = import.meta.env.VITE_WEBMAP_ID
    const web3dMapId = import.meta.env.VITE_WEBMAP_3D_ID
    const portalUrl = import.meta.env.VITE_PORTAL_URL

    if (!webMapId || !portalUrl) {
      throw new Error('WebMap configuration missing')
    }

    // Create WebMap instance
    console.log('[MapInstanceManager] Creating new WebMap instance')
    this.webMap = new WebMap({
      basemap: 'streets-navigation-vector',
      portalItem: {
        id: webMapId,
        portal: {
          url: portalUrl,
        },
      },
    })

    // Create WebScene instance with same configuration
    // WebScene can share the same portal item as WebMap
    console.log('[MapInstanceManager] Creating new WebScene instance')
    this.webScene = new WebScene({
      basemap: 'streets-navigation-vector',
      portalItem: {
        id: web3dMapId,
        portal: {
          url: portalUrl,
        },
      },
    })

    this.initialized = true
    console.log('[MapInstanceManager] Map instances initialized successfully')
  }

  /**
   * Get the appropriate map instance based on view mode
   * Both 2D and 3D views will share the same underlying map data
   */
  getMapInstance(viewMode: ViewMode): WebMap | WebScene {
    if (!this.initialized) {
      this.initialize()
    }

    if (viewMode === '3D') {
      if (!this.webScene) {
        throw new Error('WebScene not initialized')
      }
      console.log('[MapInstanceManager] Returning existing WebScene instance')
      return this.webScene
    }

    if (!this.webMap) {
      throw new Error('WebMap not initialized')
    }
    console.log('[MapInstanceManager] Returning existing WebMap instance')
    return this.webMap
  }

  /**
   * Get WebMap instance directly
   */
  getWebMap(): WebMap {
    if (!this.initialized) {
      this.initialize()
    }
    if (!this.webMap) {
      throw new Error('WebMap not initialized')
    }
    return this.webMap
  }

  /**
   * Get WebScene instance directly
   */
  getWebScene(): WebScene {
    if (!this.initialized) {
      this.initialize()
    }
    if (!this.webScene) {
      throw new Error('WebScene not initialized')
    }
    return this.webScene
  }

  /**
   * Check if 3D mode is enabled
   */
  is3DEnabled(): boolean {
    return import.meta.env.VITE_ENABLE_3D === 'true'
  }

  /**
   * Destroy instances and reset state
   * Used for cleanup or testing
   */
  destroy(): void {
    if (this.webMap) {
      this.webMap.destroy()
      this.webMap = null
    }
    if (this.webScene) {
      this.webScene.destroy()
      this.webScene = null
    }
    this.initialized = false
  }
}

// Export singleton instance
export const mapInstanceManager = MapInstanceManager.getInstance()
